import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  User,
  Trophy,
  Play,
  Users,
  Target,
} from "lucide-react";
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
} from "../../../Config/matuAuth";
import { ref, set, get } from "../../../Config/matuRtdb";
import { auth, database } from "../../../Config/firebase";

// InputField component
const InputField = ({
  label,
  type,
  placeholder,
  value,
  onChange,
  icon,
  error,
  showPassword,
  onTogglePassword,
}) => {
  const inputType = type === "password" && showPassword ? "text" : type;

  return (
    <div className="mb-6">
      <label className="block text-sm font-semibold text-gray-200 mb-2">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
          {icon}
        </div>
        <input
          type={inputType}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`w-full pl-12 pr-12 py-4 rounded-xl
           bg-white/10 backdrop-blur-sm border border-white/20
           text-white placeholder-gray-400
           focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent
           transition-all duration-300
           ${error ? "border-red-400 focus:ring-red-400" : ""}`}
        />
        {type === "password" && onTogglePassword && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
          >
            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-400 font-medium">{error}</p>
      )}
    </div>
  );
};


export function RegisterPages() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es requerido";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Mínimo 2 caracteres";
    }

    if (!formData.email) {
      newErrors.email = "El email es requerido";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email inválido";
    }

    if (!formData.password) {
      newErrors.password = "La contraseña es requerida";
    } else if (formData.password.length < 6) {
      newErrors.password = "Mínimo 6 caracteres";
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Confirma tu contraseña";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Las contraseñas no coinciden";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveUserToDatabase = async (user, additionalData = {}) => {
    try {
      const userData = {
        uid: user.uid,
        name:
          additionalData.name || user.displayName || formData.name || "Usuario",
        email: user.email || formData.email,
        createdAt: new Date().toISOString(),
        provider: additionalData.provider || "email",
        // Campos específicos para red social deportiva
        sports: [], // Deportes que practica
        favoriteTeams: [], // Equipos favoritos
        achievements: [], // Logros deportivos
        followers: 0,
        following: 0,
        posts: 0,
        profileLevel: "beginner", // beginner, intermediate, advanced, pro
        isActive: true,
        location: "",
        bio: "",
        joinedEvents: [],
        createdEvents: [],
        ...(user.photoURL && { photoURL: user.photoURL }),
      };

      const userRef = ref(database, `users/${user.uid}`);

      // Verificar si el usuario ya existe
      const userSnapshot = await get(userRef);

      if (!userSnapshot.exists()) {
        await set(userRef, userData);
        console.log("Usuario guardado en la base de datos");
      } else {
        console.log("Usuario ya existe en la base de datos");
      }

      return userData;
    } catch (error) {
      console.error("Error al guardar usuario en la base de datos:", error);
      throw error;
    }
  };

  const handleEmailRegister = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});

    try {
      // Crear usuario con email y contraseña
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );

      // Guardar datos adicionales en Realtime Database
      await saveUserToDatabase(userCredential.user, {
        name: formData.name,
        provider: "email",
      });

      console.log("Usuario registrado exitosamente");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error en registro:", error);

      // Manejar errores específicos de Firebase
      let errorMessage = "Error al crear la cuenta";

      switch (error.code) {
        case "auth/email-already-in-use":
          errorMessage = "Este email ya está registrado";
          setErrors({ email: errorMessage });
          break;
        case "auth/weak-password":
          errorMessage = "La contraseña es muy débil";
          setErrors({ password: errorMessage });
          break;
        case "auth/invalid-email":
          errorMessage = "Email inválido";
          setErrors({ email: errorMessage });
          break;
        case "auth/operation-not-allowed":
          errorMessage = "Registro con email no habilitado";
          setErrors({ general: errorMessage });
          break;
        case "auth/too-many-requests":
          errorMessage = "Demasiados intentos. Intenta más tarde";
          setErrors({ general: errorMessage });
          break;
        default:
          setErrors({ general: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const goToLogin = () => {
    navigate("/gta-app/login");
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Animated sports background */}
      <div className="fixed inset-0 bg-gradient-to-br from-green-900 via-teal-900 to-blue-900">
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/60 via-green-800/30 to-transparent" />

        {/* Floating sports icons */}
        <div className="absolute inset-0 opacity-20">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className={`absolute animate-pulse
                ${
                  i % 5 === 0
                    ? "text-green-400"
                    : i % 5 === 1
                    ? "text-teal-400"
                    : i % 5 === 2
                    ? "text-blue-400"
                    : i % 5 === 3
                    ? "text-emerald-400"
                    : "text-cyan-400"
                }`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 2}s`,
              }}
            >
              {i % 4 === 0 ? (
                <Trophy size={28} />
              ) : i % 4 === 1 ? (
                <Play size={24} />
              ) : i % 4 === 2 ? (
                <Users size={26} />
              ) : (
                <Target size={22} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Back button */}
      <button
        onClick={handleBack}
        className="fixed top-6 left-6 z-50 p-3 rounded-full bg-white/10 backdrop-blur-lg border border-white/20
                   text-white hover:bg-white/20 transition-all duration-300 ease-out
                   hover:scale-110 active:scale-95 shadow-lg group"
      >
        <ArrowLeft
          size={24}
          className="group-hover:-translate-x-1 transition-transform duration-300"
        />
      </button>

      {/* Register form */}
      <div
        className={`relative z-10 min-h-screen flex items-center justify-center p-6 py-12
                      transform transition-all duration-1000 ease-out
                      ${
                        isVisible
                          ? "translate-y-0 opacity-100"
                          : "translate-y-8 opacity-0"
                      }`}
      >
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="p-4 rounded-full bg-gradient-to-r from-green-500 to-teal-500 shadow-lg">
                <Trophy size={32} className="text-white" />
              </div>
            </div>
            <h1
              className="text-4xl md:text-5xl font-bold mb-3
                          bg-gradient-to-r from-green-300 via-teal-200 to-blue-300 bg-clip-text text-transparent
                          drop-shadow-lg"
            >
              Únete a SportConnect
            </h1>
            <p className="text-xl text-gray-200 font-medium">
              Conecta con atletas de todo el mundo
            </p>
          </div>

          {/* Error general */}
          {errors.general && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-400/50 rounded-xl text-red-300 text-center">
              {errors.general}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleEmailRegister} className="space-y-6">
            <div className="bg-white/5 backdrop-blur-2xl rounded-3xl p-8 border border-white/10 shadow-2xl">
              <InputField
                label="Nombre Completo"
                type="text"
                placeholder="Tu nombre"
                value={formData.name}
                onChange={(value) => handleInputChange("name", value)}
                icon={<User size={20} />}
                error={errors.name}
              />

              <InputField
                label="Correo Electrónico"
                type="email"
                placeholder="tu@ejemplo.com"
                value={formData.email}
                onChange={(value) => handleInputChange("email", value)}
                icon={<Mail size={20} />}
                error={errors.email}
              />

              <InputField
                label="Contraseña"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(value) => handleInputChange("password", value)}
                icon={<Lock size={20} />}
                showPassword={showPassword}
                onTogglePassword={() => setShowPassword(!showPassword)}
                error={errors.password}
              />

              <InputField
                label="Confirmar Contraseña"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={(value) =>
                  handleInputChange("confirmPassword", value)
                }
                icon={<Lock size={20} />}
                showPassword={showConfirmPassword}
                onTogglePassword={() =>
                  setShowConfirmPassword(!showConfirmPassword)
                }
                error={errors.confirmPassword}
              />
            </div>
          </form>

          {/* Footer links */}
          <div className="text-center mt-8 space-y-4">
            <div className="flex items-center justify-center gap-2 text-gray-300">
              <span>¿Ya tienes cuenta?</span>
              <button
                onClick={goToLogin}
                className="text-green-400 hover:text-green-300 font-semibold transition-colors duration-200"
              >
                Inicia sesión aquí
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
export default RegisterPages;
